import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'

type Props = {
  className?: string
  size?: number
  /** kept for API compatibility; the skin class now picks the artwork */
  tone?: 'ink' | 'white'
  showWordmark?: boolean
  spinning?: boolean
  /** wraps the wordmark; pass e.g. "hidden sm:inline-flex" to drop it on phones */
  wordmarkClass?: string
}

/**
 * The official LifeCraft4U lockup, split from the client artwork into
 * /public/brand assets. Two colourways, straight from the client files:
 * the bright set (Black BKGND) for dark skins and the deep-violet set
 * (White BKGND) for light skins. The ancestor .lc-light class switches
 * them, so one component serves every page.
 */
export function Logo({ className, size = 32, showWordmark = true, spinning = false, wordmarkClass }: Props) {
  const spin = spinning ? 'motion-safe:animate-spin-slow' : undefined
  const wordH = Math.round(size * 0.5)
  // Sizes flow through CSS vars so a responsive override (e.g. a smaller nav
  // logo at 1024–1279px) can be applied via a class + media query.
  const sizeVars = { '--logo-mark': `${size}px`, '--logo-word': `${wordH}px` } as CSSProperties
  return (
    <span className={cn('inline-flex select-none items-center gap-2.5', className)} style={sizeVars}>
      {/* on dark */}
      <img
        src="/brand/lifecraft-mark.png"
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className={cn('h-[var(--logo-mark)] w-[var(--logo-mark)] object-contain [.lc-light_&]:hidden', spin)}
        style={{ filter: 'drop-shadow(0 1px 10px rgba(10, 6, 20, 0.5))' }}
      />
      {/* on light */}
      <img
        src="/brand/lifecraft-mark-onlight.png"
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className={cn('hidden h-[var(--logo-mark)] w-[var(--logo-mark)] object-contain [.lc-light_&]:block', spin)}
      />
      {showWordmark && (
        <span className={cn('items-center', wordmarkClass ?? 'inline-flex')}>
          <img
            src="/brand/lifecraft-wordmark.png"
            alt="LifeCraft4U"
            className="h-[var(--logo-word)] object-contain [.lc-light_&]:hidden"
            style={{ filter: 'drop-shadow(0 1px 10px rgba(10, 6, 20, 0.5))' }}
          />
          <img
            src="/brand/lifecraft-wordmark-onlight.png"
            alt="LifeCraft4U"
            className="hidden h-[var(--logo-word)] object-contain [.lc-light_&]:block"
          />
        </span>
      )}
    </span>
  )
}
